import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PhoneNumberType, TeamsPhoneNumberAssignment } from '../../../../shared/models/generated/ziroModels';
import { MicrosoftAlternateLinesResource } from '../../../shared/resources/microsoft-alternate-lines.resource';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../../../button/button.component';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { TranslateService } from '@ngx-translate/core';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';

@Component({
  selector: 'ziro-teams-alternate-numbers',
  standalone: false,
  templateUrl: './teams-alternate-numbers.component.html',
})
export class TeamsAlternateNumbersComponent implements OnInit {
  private _alternateLinesResource = inject(MicrosoftAlternateLinesResource);
  private _ziroModalService = inject(ZiroModalService);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);

  tenantId = input.required<string>();
  userPrincipalName = input.required<string>();

  alternateNumberDeleted = output<void>();

  alternateNumbers = signal<TeamsPhoneNumberAssignment[]>([]);
  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  phoneNumberTypeLabels: Record<PhoneNumberType, string> = {
    [PhoneNumberType.DIRECT_ROUTING]: 'tkey;pstn_connectivity_type.direct_routing.label',
    [PhoneNumberType.MICROSOFT_CALLING_PLANS]: 'tkey;pstn_connectivity_type.ms_calling_plans.label',
    [PhoneNumberType.OPERATOR_CONNECT]: 'tkey;pstn_connectivity_type.operator_connect.label',
  };

  ngOnInit(): void {
    this._alternateLinesResource
      .getTeamsPhoneNumberAssignments(this.tenantId(), this.userPrincipalName())
      .subscribe((alternateNumbers: TeamsPhoneNumberAssignment[]) => this.alternateNumbers.set(alternateNumbers));
  }

  onDeleteClicked(alternateNumber: TeamsPhoneNumberAssignment): void {
    const formattedNumber = this._telephoneNumberFilter.transform(alternateNumber.lineUri);
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: 'text-danger',
        title: this._translateService.instant(
          'tkey;microsoft_360.view.teams.calling.alternate_numbers.delete.modal.title',
        ),
        promptBody: this._translateService.instant(
          'tkey;microsoft_360.view.teams.calling.alternate_numbers.delete.modal.text',
          {
            number: formattedNumber,
            userPrincipalName: this.userPrincipalName(),
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () =>
              this._alternateLinesResource
                .deleteTeamsPhoneNumberAssignment(
                  this.tenantId(),
                  this.userPrincipalName(),
                  alternateNumber.lineUri,
                  alternateNumber.lineUriExtension,
                )
                .pipe(
                  map(() => true),
                  catchError((error: HttpErrorResponse) => {
                    if (!error.error?.description) {
                      throw error;
                    }
                    this._toastService.push(
                      ToastTypes.ERROR,
                      ZiroIcons.EXCLAMATION,
                      'tkey;shared.toast.operation_failed.title',
                      error.error.description,
                    );
                    return of(null);
                  }),
                ),
          },
        ],
      },
    };

    this._ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe({
        next: (isDeleted: boolean) => {
          if (isDeleted) {
            this.alternateNumbers.update((alternateNumbers: TeamsPhoneNumberAssignment[]) =>
              alternateNumbers.filter((existing: TeamsPhoneNumberAssignment) => existing !== alternateNumber),
            );
            this._toastService.push(
              ToastTypes.INFO,
              `${ZiroIcons.DELETE_SMALL} text-danger`,
              'tkey;shared.toast.delete.success.title',
              'tkey;microsoft_360.view.teams.calling.alternate_numbers.toast.delete.text',
              {
                number: formattedNumber,
                userPrincipalName: this.userPrincipalName(),
              },
            );
            this.alternateNumberDeleted.emit();
          }
        },
        error: () => {},
      });
  }
}
